import React, { useState, useEffect, useRef, useCallback } from 'react'
import Typography from '../../Typography'
import styles from './DownloadImages.module.sass'
import { useParams } from 'react-router-dom'
import Order from '../../../models/Order'
import PreviewImage from '../../PreviewImage'
import Button from '../../Button'
import Spinner from '../../Spinner'

const useOrder = (orderId, email) => {
  const [order, setOrder] = useState([])

  useEffect(() => {
    Order
      .includes({
        cart: {
          orderPackages: [
            'background',
            'package',
            'student',
            'studentImage',
            { sheets: ['pose', 'background', 'foreground'] },
            { addonSheets: ['pose', 'background'] },
            { api_option: 'image_types' },
            { extras: 'prices' },
          ],
        },
      })
      .where({ for_email: [orderId, email] })
      .first()
      .then((response) => setOrder(response.data))
  }, [orderId, email])

  return order;
};

const buildItemsList = (order) => {
  const items = []
  if (!order || !order.cart || !order.cart.orderPackages) return items

  order.cart.orderPackages.forEach((orderPackage) => {
    if (!orderPackage.hasDownloadableImages) return

    if (orderPackage.sheets && orderPackage.sheets.length > 0) {
      orderPackage.sheets.filter((sheet) => sheet.hasDownloadableImage).forEach((sheet) => {
        items.push({ type: 'pose', id: sheet.pose.id })
      })
    }

    if (orderPackage.addonSheets && orderPackage.addonSheets.length > 0) {
      orderPackage.addonSheets.filter((sheet) => sheet.hasDownloadableImage).forEach((sheet) => {
        items.push({ type: 'pose', id: sheet.pose.id })
      })
    }

    if (orderPackage.studentImage && orderPackage.studentImage.imageUrl) {
      items.push({ type: 'student_image', id: orderPackage.studentImage.id })
    }
  })

  return items
}

const DownloadImages = () => {
  const { order_id, email } = useParams();
  const order = useOrder(order_id, email);
  const [availability, setAvailability] = useState({})
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true)
  const pollingRef = useRef(null)

  const checkAvailability = useCallback((items) => {
    fetch('/download/check_image_availability', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_id, email, items }),
    })
      .then((res) => res.json())
      .then((data) => {
        setAvailability(data.availability || {})
        setIsCheckingAvailability(false)

        const allReady = Object.values(data.availability || {}).every((v) => v === true)
        if (allReady && pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
      })
      .catch(() => {
        setIsCheckingAvailability(false)
      })
  }, [order_id, email])

  useEffect(() => {
    if (!order || !order.cart) return

    const items = buildItemsList(order)
    if (items.length === 0) {
      setIsCheckingAvailability(false)
      return
    }

    checkAvailability(items)

    pollingRef.current = setInterval(() => {
      checkAvailability(items)
    }, 30000)

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [order, checkAvailability])

  if (typeof order !== 'object') return null
  if (!order) return null

  const items = buildItemsList(order)
  const readyCount = Object.values(availability).filter((v) => v === true).length
  const totalCount = items.length
  const allReady = totalCount > 0 && readyCount === totalCount

  return (
    <div className={styles.container}>
      <Typography align="center" weight={500}>IMAGE DOWNLOAD</Typography>
      <Typography align="center" weight={600}>Order #{order.id}</Typography>

      {totalCount > 0 && (
        <div className={styles.progressContainer}>
          {isCheckingAvailability ? (
            <div className={styles.progressLabel}>Checking image availability...</div>
          ) : allReady ? (
            <div className={styles.allReady}>All images ready for download</div>
          ) : (
            <>
              <div className={styles.progressLabel}>{readyCount} of {totalCount} images ready</div>
              <div className={styles.progressTrack}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${(readyCount / totalCount) * 100}%` }}
                />
              </div>
            </>
          )}
        </div>
      )}

      <div className={styles.imageContainer}>
        {isCheckingAvailability && <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>}

        {!isCheckingAvailability && order && order.cart && order.cart.orderPackages.map((orderPackage) => {
          if (orderPackage.hasDownloadableImages && orderPackage.sheets.length > 0) {
            return (
              orderPackage.sheets.filter((sheet) => sheet.hasDownloadableImage).map((sheet) => {
                const baseUrl = sheet.pose.isPNG() ? '/download/download_png_image/' : '/download/download_image';
                const imageUrl = `${baseUrl}/${orderPackage.student.id}?pose_id=${sheet.pose.id}&background_id=${sheet.background && sheet.background.id}&order_package_id=${orderPackage.id}&email=${email}${sheet.foregroundId ? `&foreground_id=${sheet.foregroundId}` : ''}`;
                const itemKey = `pose-${sheet.pose.id}`
                const isReady = availability[itemKey] === true

                return (
                  <React.Fragment key={`${orderPackage.id}-sheet-${sheet.pose.id}`}>
                    <PreviewImage
                      actualForegroundUrl={sheet.foregroundId ? sheet.foreground.imageUrl : undefined}
                      foregroundUrl={sheet.pose.imageUrl}
                      backgroundUrl={sheet.background && sheet.background.imageUrl}
                      accessCodeOnly={false}
                    />
                    {isReady ? (
                      <a style={{ display: 'block' }} href={imageUrl}>
                        <Button fullWidth variant="primary">
                          Download Image Above
                        </Button>
                      </a>
                    ) : (
                      <Button fullWidth variant="primary" disabled>
                        Preparing Image...
                      </Button>
                    )}
                    <br />
                  </React.Fragment>
                )
              })
            )
          }

          if (orderPackage.hasDownloadableImages && orderPackage.addonSheets.length > 0) {
            return (
              orderPackage.addonSheets.filter((sheet) => sheet.hasDownloadableImage).map((sheet) => {
                const baseUrl = sheet.pose.isPNG() ? '/download/download_png_image/' : '/download/download_image'
                const imageUrl = `${baseUrl}/${orderPackage.student.id}?pose_id=${sheet.pose.id}&background_id=${sheet.background && sheet.background.id}&order_package_id=${orderPackage.id}&email=${email}`
                const itemKey = `pose-${sheet.pose.id}`
                const isReady = availability[itemKey] === true

                return (
                  <React.Fragment key={`${orderPackage.id}-addon-${sheet.pose.id}`}>
                    <PreviewImage
                      foregroundUrl={sheet.pose.imageUrl}
                      backgroundUrl={sheet.background && sheet.background.imageUrl}
                      accessCodeOnly={false}
                    />
                    {isReady ? (
                      <a style={{ display: 'block' }} href={imageUrl}>
                        <Button fullWidth variant="primary">
                          Download Image Above
                        </Button>
                      </a>
                    ) : (
                      <Button fullWidth variant="primary" disabled>
                        Preparing Image...
                      </Button>
                    )}
                    <br />
                  </React.Fragment>
                )
              })
            )
          }

          if (orderPackage.studentImage && orderPackage.studentImage.imageUrl && orderPackage.hasDownloadableImages) {
            const baseUrl = orderPackage.studentImage.isPNG() && orderPackage.backgroundId ? '/download/download_png_image/' : '/download/download_image'
            const imageUrl = `${baseUrl}/${orderPackage.student.id}?student_image_id=${orderPackage.studentImage.id}&background_id=${orderPackage.background && orderPackage.background.id}&order_package_id=${orderPackage.id}&email=${email}`
            const itemKey = `student_image-${orderPackage.studentImage.id}`
            const isReady = availability[itemKey] === true

            return (
              <React.Fragment key={orderPackage.id}>
                <PreviewImage
                  foregroundUrl={orderPackage.studentImage.imageUrl}
                  backgroundUrl={orderPackage.background && orderPackage.background.imageUrl}
                  accessCodeOnly={false}
                />
                {isReady ? (
                  <a style={{ display: 'block' }} href={imageUrl}>
                    <Button fullWidth variant="primary">
                      Download
                    </Button>
                  </a>
                ) : (
                  <Button fullWidth variant="primary" disabled>
                    Preparing Image...
                  </Button>
                )}
              </React.Fragment>
            )
          }

          return null
        })}
      </div>
    </div>
  );
};

export default DownloadImages;
